import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import { Box, Card, Tab, Tabs, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { truncate } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ImageHandler } from '../../../../../../shared/components/ImageHandler';
import { EEmployeeContract } from '../../../../../../shared/enums/user/EContract';
import { ERole } from '../../../../../../shared/enums/user/ERole';
import { ETheme } from '../../../../../../shared/enums/user/ETheme';
import { formatDate } from '../../../../../../shared/functions/formatDate';
import { handleError } from '../../../../../../shared/functions/handleError';
import { IDepartment } from '../../../../../../shared/interfaces/department/IDepartment';
import { IFormField } from '../../../../../../shared/interfaces/forms/IForms';
import { IProject } from '../../../../../../shared/interfaces/projects/IProject';
import {
  IPicture,
  ISkill,
  IUser,
} from '../../../../../../shared/interfaces/user/IUser';
import {
  darkPalette,
  lightPalette,
} from '../../../../../../shared/mui/palettes';
import { RootState } from '../../../../../../store/store';
import { getFileType } from '../../../components/tabs/Documents';
import {
  a11yProps,
  CustomTabPanel,
} from '../../../components/tabs/EmployeeTabs';
import { AdminEmployeeService } from '../../../service';

interface IProps {
  user: IUser;
}
const MyProfileTab = ({ user }: IProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { userData } = useSelector((state: RootState) => state?.auth);
  const { theme, id: userId } = userData as IUser;
  const isNormalUser = userData?.role === ERole.USER;
  const hidePersonalField = isNormalUser && userId !== Number(id);
  const [value, setValue] = useState(0);
  const [formFields, setFormFields] = useState<{
    1: IFormField[];
    2: IFormField[];
    3: IFormField[];
    4: IFormField[];
    5: IFormField[];
  }>({ 1: [], 2: [], 3: [], 4: [], 5: [] });
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const fileInputKey = ['profilePhoto', 'userDocuments'];
  const getUserKey = (user: IUser, userKey: string): string => {
    const value = String(user[userKey as keyof typeof user]);
    const normalProperty = user[userKey as keyof typeof user];
    const transformations: Record<string, (val: string) => string> = {
      maritalStatus: (val) => t(`maritalStatus.${val}`),
      gender: (val) => t(`gender.${val}`),
      religion: (val) => t(`religion.${val}`),
      country: (val) => t(`country.${val}`),
      theme: (val) => t(`theme.${val}`),
      workMode: (val) => t(`workMode.${val}`),
      education: (val) => t(`education.${val}`),
      employeeContract: (val) => t(`employeeContract.${val}`),
      employeeType: (val) => t(`employeeType.${val}`),
      about: (val) => truncate(val, { length: 20 }),
      speakingLanguage: () =>
        (normalProperty as string[])
          ?.map((item) => t(`language.${item}`))
          ?.join(', '),
      departments: () =>
        (normalProperty as IDepartment[])?.map((item) => item.name)?.join(', '),
      skills: () =>
        (normalProperty as ISkill[])?.map((item) => item.name)?.join(', '),
      projects: () =>
        (normalProperty as IProject[])?.map((item) => item.name)?.join(', '),
      dateOfJoin: (val) => formatDate(val),
      birthday: (val) => formatDate(val),
    };

    return transformations[userKey]?.(value) ?? value;
  };
  useEffect(() => {
    !!id &&
      AdminEmployeeService.getFormFields(Number(id))
        .then((response) => {
          setFormFields(response);
        })
        .catch((err) => handleError(err));
  }, [id]);
  return (
    <Card>
      <Box
        sx={{
          borderBottom: 1,
          borderColor:
            theme === ETheme.LIGHT
              ? lightPalette.primary.light
              : darkPalette.background.light,
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          aria-label="basic tabs example"
        >
          <Tab
            icon={<Person2OutlinedIcon />}
            label="Personal information"
            {...a11yProps(0)}
          />
          <Tab
            icon={<BusinessCenterOutlinedIcon />}
            label="Professional information"
            {...a11yProps(1)}
          />
          <Tab
            icon={<ArticleOutlinedIcon />}
            label="Documents"
            {...a11yProps(2)}
          />
          <Tab
            icon={<LockOutlinedIcon />}
            label="Account access"
            {...a11yProps(3)}
          />
          {!hidePersonalField && (
            <Tab
              icon={<BeachAccessOutlinedIcon />}
              label="Leave days balance"
              {...a11yProps(4)}
            />
          )}
        </Tabs>
      </Box>
      {[...Array(5)].map((_, i) => (
        <CustomTabPanel value={value} index={i}>
          <Grid container spacing={2} key={i}>
            {formFields[(i + 1) as keyof typeof formFields]?.map((field) => {
              if (field.key === 'contractEndDate') {
                return user?.employeeContract === EEmployeeContract.FIXED_TERM;
              }
              const isFileInput = fileInputKey?.includes(field?.key);
              const userFiles = user?.[
                field?.key as keyof typeof user
              ] as IPicture[];

              return (
                <Grid
                  key={field?.key}
                  sx={{
                    py: { md: '10px' },
                    borderBottom: `1px solid ${theme === ETheme.LIGHT ? lightPalette.primary.light : darkPalette.background.light}`,
                  }}
                  size={{ md: 6, xs: 12 }}
                >
                  <Typography
                    variant={'subtitle1'}
                    color={lightPalette.text.gray}
                    sx={{
                      fontWeight: '300',
                    }}
                  >
                    {t(`general.${field?.key}`)}
                  </Typography>
                  {isFileInput ? (
                    <>
                      {userFiles
                        ? (Array.isArray(userFiles)
                            ? userFiles
                            : [userFiles]
                          )?.map((document) => {
                            return (
                              <ImageHandler
                                key={document?.id}
                                src={document?.path}
                                alt={`File preview - ${document?.id}`}
                                onClick={() => window.open(document?.path)}
                                className={'profile-backup-image'}
                                style={{
                                  borderColor:
                                    theme === ETheme.LIGHT
                                      ? lightPalette.primary.light
                                      : darkPalette.background.paper,
                                }}
                                tooltipTitle={document?.name}
                                fallbackType={getFileType(document?.mime)}
                              />
                            );
                          })
                        : '-'}
                    </>
                  ) : (
                    <Typography variant={'body1'}>
                      {!!getUserKey(user, field?.key)?.length
                        ? getUserKey(user, field?.key)
                        : '-'}
                    </Typography>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </CustomTabPanel>
      ))}
    </Card>
  );
};

export default MyProfileTab;
